import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Stage, Layer, Rect, Circle, Line, Text, Group, Shape } from 'react-konva';
import Konva from 'konva';
import { useTranslation } from '@structura/i18n';
import { createWoodPattern, createGlulamPattern, createConcretePattern, createSteelPattern } from './MaterialPatterns';

interface CrossSection {
  name: string;
  width: number;  // mm
  height: number; // mm
  area: number;   // m²
  inertia: number; // m⁴
  isCustom: boolean;
  key?: string;
  material?: string;
  sectionType?: 'I-beam' | 'H-beam' | 'rectangular' | 'circular' | 'T-beam' | 'L-beam';
  webThickness?: number; // mm - for I/H beams
  flangeThickness?: number; // mm - for I/H beams
  sectionClass?: 'auto' | '1' | '2' | '3' | '4';
}

interface CrossSectionKonvaProps {
  crossSection: CrossSection;
  material?: string;
  timberKind?: 'glulam' | 'solid';
  width?: number;
  height?: number;
  showDimensions?: boolean;
  rebar?: {
    cover: number;
    layers: Array<{ z: number; diameter: number; count: number }>;
    stirrups?: { diameter: number; spacing: number; legs?: number };
  };
  storageKey?: string;
  provideCapture?: (capture: () => string) => void;
}

const CrossSectionKonva: React.FC<CrossSectionKonvaProps> = ({
  crossSection,
  material,
  timberKind,
  width: initialWidth = 400,
  height: initialHeight = 300,
  showDimensions = true,
  rebar,
  storageKey = 'crossSection_size',
  provideCapture,
}) => {
  const { t } = useTranslation();
  const stageRef = useRef<Konva.Stage | null>(null);

  useEffect(() => {
    if (!provideCapture) return;
    const capture = () => {
      try {
        return stageRef.current?.toDataURL({ pixelRatio: 2, mimeType: 'image/png' }) || '';
      } catch {
        return '';
      }
    };
    provideCapture(capture);
  }, [provideCapture]);

  // Load saved dimensions from localStorage (lazy initialization)
  const [stageSize, setStageSize] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { 
          width: Math.max(200, parsed.width || initialWidth), 
          height: Math.max(150, parsed.height || initialHeight) 
        };
      }
    } catch (e) {
      console.warn('Failed to load saved cross-section size:', e);
    }
    return { width: initialWidth, height: initialHeight };
  });

  const [zoom, setZoom] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Save dimensions to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(stageSize));
    } catch (e) {
      console.warn('Failed to save cross-section size:', e);
    }
  }, [stageSize, storageKey]);

  // Calculate scale to fit section in canvas
  const { scale, centerX, centerY, displayWidth, displayHeight } = useMemo(() => {
    if (!crossSection) {
      return { scale: 1, centerX: stageSize.width / 2, centerY: stageSize.height / 2, displayWidth: 0, displayHeight: 0 };
    }
    const padding = 40;
    const maxDim = Math.max(crossSection.width, crossSection.height);
    const availableSpace = Math.min(stageSize.width - padding, stageSize.height - padding);
    const calculatedScale = availableSpace / maxDim;

    return {
      scale: calculatedScale,
      centerX: stageSize.width / 2,
      centerY: stageSize.height / 2,
      displayWidth: crossSection.width * calculatedScale,
      displayHeight: crossSection.height * calculatedScale,
    };
  }, [crossSection, stageSize]);

  // Create material pattern images
  const [patternImage, setPatternImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!crossSection) return;
    const rawMat = material || crossSection.material || 'steel';
    const mat = rawMat.toLowerCase();
    let patternDataUrl: string;

    // Create pattern based on material
    if (mat === 'timber') {
      // Prefer explicit timberKind when provided; fall back to name/key convention (GL* = glulam)
      const name = (crossSection.name || '').toUpperCase();
      const key = (crossSection.key || '').toUpperCase();
      const explicitGlulam = timberKind === 'glulam';
      const inferredGlulam = name.startsWith('GL') || key.startsWith('GL');
      const isGlulam = explicitGlulam || inferredGlulam;
      patternDataUrl = isGlulam
        ? createGlulamPattern({ width: 100, height: 100, scale: 1 })
        : createWoodPattern({ width: 100, height: 100, scale: 1 });
    } else if (mat === 'concrete') {
      patternDataUrl = createConcretePattern({ width: 100, height: 100, scale: 1 });
    } else if (mat === 'steel') {
      patternDataUrl = createSteelPattern({ width: 100, height: 100, scale: 1 }, 'solid');
    } else {
      // Default pattern
      patternDataUrl = createSteelPattern({ width: 100, height: 100, scale: 1 }, 'solid');
    }

    // Load pattern image
    const img = new Image();
    img.onload = () => {
      setPatternImage(img);
    };
    img.src = patternDataUrl;
  }, [material, crossSection, timberKind]);

  // Get material colors (for stroke and fallback)
  const { fillColor, strokeColor } = useMemo(() => {
    const mat = material || crossSection?.material || 'steel';
    let fill = '#e9ecef';
    let stroke = '#6c757d';

    if (mat === 'steel') {
      fill = '#f8f9fa';
      stroke = '#495057';
    } else if (mat === 'timber') {
      fill = '#c19a6b'; // Camel/wood brown
      stroke = '#8b4513'; // Saddle brown
    } else if (mat === 'concrete') {
      fill = '#d6d8db';
      stroke = '#6c757d';
    }

    return { fillColor: fill, strokeColor: stroke };
  }, [material, crossSection?.material]);

  // Resize handler
  const handleResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = stageSize.width;
    const startHeight = stageSize.height;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(200, startWidth + (e.clientX - startX));
      const newHeight = Math.max(150, startHeight + (e.clientY - startY));
      setStageSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [stageSize]);

  // Zoom handler (mouse wheel)
  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.1;
    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(0.5, Math.min(5, newScale));

    setZoom(clampedScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
  }, []);

  // Reset view
  const resetView = useCallback(() => {
    setZoom(1);
    setStagePos({ x: 0, y: 0 });
  }, []);

  // Fit all
  const fitAll = useCallback(() => {
    setZoom(1);
    setStagePos({ x: 0, y: 0 });
  }, []);

  // Early return if no crossSection (after all hooks)
  if (!crossSection) {
    return (
      <div style={{
        width: `${stageSize.width}px`,
        height: `${stageSize.height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <span style={{ color: '#6c757d' }}>{t('beam.viz.noCrossSection')}</span>
      </div>
    );
  }

  // Render I-beam
  const renderIBeam = () => {
    const webThickness = (crossSection.webThickness || 6) * scale;
    const flangeThickness = (crossSection.flangeThickness || 10) * scale;

    return (
      <Group>
        {/* Top flange */}
        <Rect
          x={centerX - displayWidth / 2}
          y={centerY - displayHeight / 2}
          width={displayWidth}
          height={flangeThickness}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
        {/* Web */}
        <Rect
          x={centerX - webThickness / 2}
          y={centerY - displayHeight / 2}
          width={webThickness}
          height={displayHeight}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
        {/* Bottom flange */}
        <Rect
          x={centerX - displayWidth / 2}
          y={centerY + displayHeight / 2 - flangeThickness}
          width={displayWidth}
          height={flangeThickness}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
      </Group>
    );
  };

  // Render H-beam
  const renderHBeam = () => {
    const webThickness = (crossSection.webThickness || 10) * scale;
    const flangeThickness = (crossSection.flangeThickness || 15) * scale;

    return (
      <Group>
        {/* Top flange */}
        <Rect
          x={centerX - displayWidth / 2}
          y={centerY - displayHeight / 2}
          width={displayWidth}
          height={flangeThickness}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
        {/* Web */}
        <Rect
          x={centerX - webThickness / 2}
          y={centerY - displayHeight / 2}
          width={webThickness}
          height={displayHeight}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
        {/* Bottom flange */}
        <Rect
          x={centerX - displayWidth / 2}
          y={centerY + displayHeight / 2 - flangeThickness}
          width={displayWidth}
          height={flangeThickness}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
      </Group>
    );
  };

  // Render T-beam
  const renderTBeam = () => {
    const webThickness = (crossSection.webThickness || crossSection.width * 0.5) * scale;
    const flangeThickness = (crossSection.flangeThickness || crossSection.height * 0.25) * scale;

    return (
      <Group>
        {/* Top flange (full width) */}
        <Rect
          x={centerX - displayWidth / 2}
          y={centerY - displayHeight / 2}
          width={displayWidth}
          height={flangeThickness}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
        {/* Web (centered, extending down) */}
        <Rect
          x={centerX - webThickness / 2}
          y={centerY - displayHeight / 2}
          width={webThickness}
          height={displayHeight}
          fillPatternImage={patternImage || undefined}
          fillPatternRepeat="repeat"
          fill={patternImage ? undefined : fillColor}
          stroke={strokeColor}
          strokeWidth={2}
        />
      </Group>
    );
  };

  // Render rectangular section
  const renderRectangular = () => {
    return (
      <Rect
        x={centerX - displayWidth / 2}
        y={centerY - displayHeight / 2}
        width={displayWidth}
        height={displayHeight}
        fillPatternImage={patternImage || undefined}
        fillPatternRepeat="repeat"
        fill={patternImage ? undefined : fillColor}
        stroke={strokeColor}
        strokeWidth={2}
      />
    );
  };

  // Render circular section
  const renderCircular = () => {
    const diameter = Math.min(displayWidth, displayHeight);
    return (
      <Circle
        x={centerX}
        y={centerY}
        radius={diameter / 2}
        fillPatternImage={patternImage || undefined}
        fillPatternRepeat="repeat"
        fill={patternImage ? undefined : fillColor}
        stroke={strokeColor}
        strokeWidth={2}
      />
    );
  };

  // Render section based on type
  const renderSection = () => {
    switch (crossSection.sectionType) {
      case 'I-beam':
        return renderIBeam();
      case 'H-beam':
        return renderHBeam();
      case 'T-beam':
        return renderTBeam();
      case 'circular':
        return renderCircular();
      case 'rectangular':
      default:
        return renderRectangular();
    }
  };

  // Render reinforcement bars (dark blue circles)
  const renderRebars = () => {
    if (!rebar || !rebar.layers || rebar.layers.length === 0) return null;
    const cover = Math.max(0, rebar.cover ?? 0);

    const elements: any[] = [];
    for (let i = 0; i < rebar.layers.length; i++) {
      const layer = rebar.layers[i];
      const d = Math.max(1, layer.diameter || 0);
      const n = Math.max(1, layer.count || 1);

      // clamp z within concrete
      const zLimit = (crossSection.height / 2) - cover - d / 2;
      const zClamped = Math.max(-zLimit, Math.min(zLimit, layer.z || 0));

      const radius = (d / 2) * scale;
      const y = centerY - (zClamped * scale);

      // horizontal placement inside cover
      const leftX = centerX - displayWidth / 2 + (cover * scale) + radius;
      const rightX = centerX + displayWidth / 2 - (cover * scale) - radius;
      let xs: number[] = [];
      if (n <= 1) {
        xs = [ (leftX + rightX) / 2 ];
      } else {
        const step = (rightX - leftX) / (n - 1);
        for (let k = 0; k < n; k++) xs.push(leftX + k * step);
      }

      for (let k = 0; k < xs.length; k++) {
        elements.push(
          <Circle
            key={`rebar-${i}-${k}`}
            x={xs[k]}
            y={y}
            radius={radius}
            fill="#0d47a1"
            stroke="#0b3a83"
            strokeWidth={1}
          />
        );
      }
    }
    return <Group>{elements}</Group>;
  };

  // Render closed stirrup rectangle inside cover
  const renderStirrups = () => {
    if (!rebar || !rebar.stirrups) return null;
    const cover = Math.max(0, rebar.cover ?? 0);
    const d = Math.max(6, rebar.stirrups.diameter || 6);
    const leg = Math.max(2, rebar.stirrups.legs || 2);

    // Inner rectangle for stirrup centerline
    const insetPx = 4; // draw stirrups slightly outward from bars for visibility
    const x1 = centerX - displayWidth / 2 + (cover * scale) + (d / 2) * scale - insetPx;
    const x2 = centerX + displayWidth / 2 - (cover * scale) - (d / 2) * scale + insetPx;
    const y1 = centerY - displayHeight / 2 + (cover * scale) + (d / 2) * scale - insetPx;
    const y2 = centerY + displayHeight / 2 - (cover * scale) - (d / 2) * scale + insetPx;

    const rMax = Math.min((x2 - x1), (y2 - y1)) / 4;
    const r = Math.max(2, Math.min(rMax, d * scale));

    return (
      <Group>
        <Shape
          sceneFunc={(ctx, shape) => {
            ctx.beginPath();
            ctx.moveTo(x1 + r, y1);
            ctx.arcTo(x2, y1, x2, y1 + r, r);
            ctx.arcTo(x2, y2, x2 - r, y2, r);
            ctx.arcTo(x1, y2, x1, y2 - r, r);
            ctx.arcTo(x1, y1, x1 + r, y1, r);
            ctx.closePath();
            ctx.strokeShape(shape);
          }}
          stroke="#0b3a83"
          strokeWidth={3}
        />
        {/* draw additional legs if leg > 2 by parallel lines near flanges */}
        {leg > 2 && (
          <>
            <Line points={[x1 + (d * scale), y1, x2 - (d * scale), y1]} stroke="#0b3a83" strokeWidth={3} />
            <Line points={[x1 + (d * scale), y2, x2 - (d * scale), y2]} stroke="#0b3a83" strokeWidth={3} />
          </>
        )}
      </Group>
    );
  };

  // Render dimensions
  const renderDimensions = () => {
    if (!showDimensions) return null;

    return (
      <Group>
        {/* Width dimension line */}
        <Line
          points={[
            centerX - displayWidth / 2,
            centerY + displayHeight / 2 + 15,
            centerX + displayWidth / 2,
            centerY + displayHeight / 2 + 15,
          ]}
          stroke="#007bff"
          strokeWidth={1}
          dash={[5, 5]}
        />
        {/* Width dimension text */}
        <Text
          x={centerX}
          y={centerY + displayHeight / 2 + 20}
          text={`${crossSection.width}mm`}
          fontSize={12}
          fill="#007bff"
          offsetX={25}
          align="center"
        />

        {/* Height dimension line */}
        <Line
          points={[
            centerX + displayWidth / 2 + 15,
            centerY - displayHeight / 2,
            centerX + displayWidth / 2 + 15,
            centerY + displayHeight / 2,
          ]}
          stroke="#007bff"
          strokeWidth={1}
          dash={[5, 5]}
        />
        {/* Height dimension text (rotated) */}
        <Text
          x={centerX + displayWidth / 2 + 20}
          y={centerY}
          text={`${crossSection.height}mm`}
          fontSize={12}
          fill="#007bff"
          rotation={-90}
          offsetX={25}
          offsetY={-5}
          align="center"
        />
      </Group>
    );
  };

  return (
    <div style={{ position: 'relative', width: 'fit-content', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#fff', overflow: 'hidden' }}>
      {/* Control buttons */}
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 10,
        display: 'flex',
        gap: '4px'
      }}>
        <button
          onClick={fitAll}
          className="btn btn-sm btn-outline-primary"
          title={t('beam.viz.fitAll')}
          style={{ fontSize: '12px', padding: '2px 6px' }}
        >
          📐
        </button>
        <button
          onClick={resetView}
          className="btn btn-sm btn-outline-secondary"
          title={t('beam.viz.resetView')}
          style={{ fontSize: '12px', padding: '2px 6px' }}
        >
          🔄
        </button>
        <span className="badge bg-secondary" style={{ fontSize: '10px', padding: '2px 4px' }}>
          {Math.round(zoom * 100)}%
        </span>
      </div>

      <Stage
        width={stageSize.width}
        height={stageSize.height}
        scaleX={zoom}
        scaleY={zoom}
        x={stagePos.x}
        y={stagePos.y}
        onWheel={handleWheel}
        draggable
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(e) => {
          setIsDragging(false);
          setStagePos({ x: e.target.x(), y: e.target.y() });
        }}
        style={{ 
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}
        ref={(node) => { stageRef.current = node as unknown as Konva.Stage; }}
      >
        <Layer>
          {renderSection()}
          {renderDimensions()}
          {renderStirrups()}
          {renderRebars()}
        </Layer>
      </Stage>

      {/* Resize handle */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '16px',
          height: '16px',
          background: 'linear-gradient(135deg, transparent 50%, #6c757d 50%)',
          cursor: 'se-resize',
          opacity: 0.6,
          zIndex: 10
        }}
        onMouseDown={handleResize}
        title={t('beam.viz.dragToResize')}
      />

      {/* Instructions */}
      <div style={{
        position: 'absolute',
        bottom: '4px',
        left: '8px',
        fontSize: '10px',
        color: '#6c757d',
        pointerEvents: 'none',
        userSelect: 'none'
      }}>
        Scroll: Zoom • Drag: Pan • Corner: Resize
      </div>
    </div>
  );
};

export default CrossSectionKonva;

